import type { LeadIncomingCallsResult } from 'lib/lead-incoming-calls';
import { useEffect, useState } from 'react';

import { useSession } from 'providers/session';

type UseLeadIncomingCallsOptions = {
  phone?: string | null;
  userId?: string | null;
  excludeIds?: string[];
  enabled?: boolean;
};

export default function useLeadIncomingCalls({
  phone,
  userId,
  excludeIds = [],
  enabled = true,
}: UseLeadIncomingCallsOptions) {
  const { token } = useSession();
  const [data, setData] = useState<LeadIncomingCallsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const excludeKey = excludeIds.filter(Boolean).join(',');

  useEffect(() => {
    if (!enabled || !token || (!phone?.trim() && !userId?.trim())) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();

        if (phone?.trim()) {
          params.set('phone', phone.trim());
        }

        if (userId?.trim()) {
          params.set('userId', userId.trim());
        }

        if (excludeKey) {
          params.set('excludeIds', excludeKey);
        }

        const response = await fetch(
          `/api/leads/incoming-calls?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload?.error?.message || 'Failed to load incoming calls.',
          );
        }

        if (!cancelled) {
          setData(payload);
        }
      } catch (fetchError: any) {
        if (!cancelled) {
          setData(null);
          setError(fetchError?.message || 'Failed to load incoming calls.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [phone, userId, excludeKey, enabled, token]);

  return { data, loading, error };
}
